import type { GameEvent } from '../schema';

/** Stress, burnout, rest, leisure and recovery — the wellbeing loop. */
export const WELLBEING_EVENTS: GameEvent[] = [
  {
    id: 'wellbeing.earlyWarning',
    title: 'You are tired in a new way',
    description:
      'Not sleepy. Flat. The things that used to be interesting are now items on a list.',
    category: 'wellbeing',
    pools: ['pool.stress', 'pool.burnout'],
    weight: 9,
    cooldown: 3,
    requires: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 40 }],
    weightMods: [{ conditions: [{ k: 'burnout', anyOf: ['overextended', 'exhausted'] }], multiplier: 1.8 }],
    choices: [
      {
        id: 'rest',
        label: 'Take it seriously now',
        detail: 'Cut the load. Book the leave. Say no to things.',
        hint: 'costly',
        tags: ['rest', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Pulled back in time',
            narrative: 'Two months of deliberately less. Nobody noticed except you, which is the point.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -14 },
              { k: 'burnout', op: 'load', delta: -24 },
              { k: 'stat', stat: 'stress', delta: -14 },
              { k: 'stat', stat: 'energy', delta: 14 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -4 },
              { k: 'habit', habit: 'workaholism', delta: -10 },
            ],
          },
        ],
      },
      {
        id: 'push',
        label: 'Push through it',
        hint: 'risky',
        tags: ['denial'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept going',
            narrative: 'You have done this before and it worked. It is worth noticing that the recoveries are getting shorter.',
            tone: 'negative',
            effects: [
              { k: 'burnout', op: 'load', delta: 16 },
              { k: 'stat', stat: 'energy', delta: -10 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'habit', habit: 'workaholism', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'help',
        label: 'Talk to someone',
        hint: 'safe',
        tags: ['support'],
        outcomes: [
          {
            weight: 1,
            label: 'Said it out loud',
            narrative: 'Naming it to another person removed a surprising amount of its weight.',
            tone: 'positive',
            effects: [
              { k: 'burnout', op: 'load', delta: -12 },
              { k: 'stat', stat: 'stress', delta: -10 },
              { k: 'relationshipAll', dim: 'trust', delta: 5 },
              { k: 'relationshipAll', dim: 'support', delta: 6 },
              { k: 'cashIncomeMonths', months: -0.4, note: 'therapy' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.burnoutHit',
    title: 'You have stopped',
    description:
      'Not a decision. You simply cannot make yourself do it any more, and the attempt makes it worse.',
    category: 'wellbeing',
    pools: ['pool.burnout'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'stop',
        label: 'Stop',
        outcomes: [
          {
            weight: 1,
            label: 'Burnt out',
            narrative: 'Signed off. The relief is immediate and the shame arrives about a week later.',
            tone: 'negative',
            effects: [
              { k: 'burnout', op: 'setStage', stage: 'burntOut' },
              { k: 'stat', stat: 'energy', delta: -30 },
              { k: 'stat', stat: 'happiness', delta: -20 },
              { k: 'stat', stat: 'purpose', delta: -16 },
              { k: 'stat', stat: 'health', delta: -8 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -20 },
              { k: 'salary', mode: 'mult', value: 0.85 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'memory', title: 'Burnt out', tone: 'negative', category: 'wellbeing', weight: 10 },
              { k: 'triggerEvent', eventId: 'wellbeing.recoveryPath' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.recoveryPath',
    title: 'How do you come back?',
    description: 'You cannot return to exactly what broke you. Every route out costs something different.',
    category: 'wellbeing',
    pools: ['pool.recovery'],
    weight: 0,
    priority: 9,
    choices: [
      {
        id: 'longLeave',
        label: 'Take a long, real break',
        detail: 'Months, not weeks. Unpaid if necessary.',
        hint: 'costly',
        tags: ['rest', 'recovery'],
        outcomes: [
          {
            weight: 1,
            label: 'Took the time',
            narrative: 'Nine months of very little. It was the only thing that worked, and it worked completely.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'setStatus', status: 'break' },
              { k: 'cashIncomeMonths', months: -6, note: 'extended leave' },
              { k: 'burnout', op: 'setStage', stage: 'recovering' },
              { k: 'burnout', op: 'load', delta: -40 },
              { k: 'stat', stat: 'energy', delta: 22 },
              { k: 'stat', stat: 'stress', delta: -25 },
              { k: 'habit', habit: 'workaholism', delta: -20 },
              { k: 'schedule', afterYears: 2, label: 'Returning to work', eventId: 'wellbeing.returnToWork', effects: [] },
            ],
          },
        ],
      },
      {
        id: 'downshift',
        label: 'Go back, but smaller',
        detail: 'Same field, less seniority, fewer hours.',
        hint: 'mixed',
        tags: ['recovery', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Downshifted',
            narrative: 'A step backwards on paper. Your evenings returned and you stopped dreading Sundays.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'demote', levels: 1 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -24 },
              { k: 'burnout', op: 'setStage', stage: 'rebuilding' },
              { k: 'burnout', op: 'load', delta: -28 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'stat', stat: 'energy', delta: 14 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'changeEverything',
        label: 'Change the field entirely',
        hint: 'risky',
        tags: ['change', 'courage'],
        outcomes: [
          {
            weight: 58,
            label: 'A different life',
            narrative: 'Lower pay, unrecognisable work, and the first genuine enthusiasm in six years.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'adaptability', op: '>=', value: 58 }], multiplier: 1.7 },
              { conditions: [{ k: 'num', path: 'supportNetwork', op: '>=', value: 55 }], multiplier: 1.5 },
            ],
            effects: [
              { k: 'career', op: 'setStatus', status: 'unemployed' },
              { k: 'burnout', op: 'setStage', stage: 'rebuilding' },
              { k: 'burnout', op: 'load', delta: -35 },
              { k: 'stat', stat: 'purpose', delta: 20 },
              { k: 'trait', trait: 'adaptability', delta: 8 },
              { k: 'triggerEvent', eventId: 'career.pivotOffer', afterYears: 1 },
            ],
          },
          {
            weight: 42,
            label: 'Adrift',
            narrative: 'You left and did not arrive anywhere for two years.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'setStatus', status: 'unemployed' },
              { k: 'cashIncomeMonths', months: -9, note: 'extended unemployment' },
              { k: 'burnout', op: 'load', delta: -18 },
              { k: 'stat', stat: 'purpose', delta: -10 },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'triggerEvent', eventId: 'career.unemployed', afterYears: 1 },
            ],
          },
        ],
      },
      {
        id: 'rushBack',
        label: 'Go straight back',
        detail: 'You cannot afford this.',
        hint: 'risky',
        tags: ['denial'],
        outcomes: [
          {
            weight: 1,
            label: 'Returned unhealed',
            narrative: 'Four weeks off and back at full load. The second collapse is usually worse.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'burnout', op: 'setStage', stage: 'atRisk' },
              { k: 'burnout', op: 'load', delta: -8 },
              { k: 'stat', stat: 'health', delta: -8 },
              { k: 'schedule', afterYears: 3, label: 'The second collapse', requires: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 55 }], eventId: 'wellbeing.burnoutHit', effects: [] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.returnToWork',
    title: 'Ready to work again',
    description: 'The energy is back. The question is what you do with it this time.',
    category: 'wellbeing',
    pools: ['pool.recovery'],
    weight: 0,
    choices: [
      {
        id: 'boundaried',
        label: 'Return with firm limits',
        hint: 'safe',
        tags: ['boundaries', 'recovery'],
        outcomes: [
          {
            weight: 1,
            label: 'Came back differently',
            narrative: 'Same work, radically different relationship to it. You leave at five and mean it.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'workload', delta: -12 },
              { k: 'burnout', op: 'setStage', stage: 'rebuilding' },
              { k: 'habit', habit: 'workaholism', delta: -16 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'achievement', id: 'ach.recovered', label: 'Recovered from burnout', category: 'wellbeing' },
            ],
          },
        ],
      },
      {
        id: 'ambitious',
        label: 'Return and make up for lost time',
        hint: 'risky',
        tags: ['ambition'],
        outcomes: [
          {
            weight: 1,
            label: 'Back at full speed',
            narrative: 'You are behind and you know it. The catch-up instinct is the same instinct that caused this.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'performance', delta: 10 },
              { k: 'career', op: 'adjust', field: 'workload', delta: 14 },
              { k: 'burnout', op: 'load', delta: 14 },
              { k: 'burnout', op: 'setStage', stage: 'overextended' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.vacation',
    title: 'Somewhere else for two weeks',
    description: 'Real leave, actually taken, phone in a drawer. It costs money and a fortnight of momentum.',
    category: 'leisure',
    pools: ['pool.leisure'],
    weight: 8,
    cooldown: 2,
    choices: [
      {
        id: 'go',
        label: 'Go',
        hint: 'safe',
        tags: ['rest', 'exploration'],
        cost: [{ k: 'cashIncomeMonths', months: -0.7, note: 'holiday' }],
        outcomes: [
          {
            weight: 1,
            label: 'Went away',
            narrative: 'You came back with a different nervous system.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'energy', delta: 16 },
              { k: 'stat', stat: 'stress', delta: -16 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'burnout', op: 'load', delta: -12 },
              { k: 'counter', name: 'travelTrips', delta: 1 },
              { k: 'relationshipAll', dim: 'closeness', delta: 6 },
              { k: 'aspiration', op: 'progress', defId: 'dream.travel', delta: 18 },
            ],
          },
        ],
      },
      {
        id: 'staycation',
        label: 'Take the leave, stay home',
        hint: 'safe',
        tags: ['rest'],
        outcomes: [
          {
            weight: 1,
            label: 'Rested at home',
            narrative: 'Cheaper, quieter, and you did end up answering two emails.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'energy', delta: 9 },
              { k: 'stat', stat: 'stress', delta: -9 },
              { k: 'burnout', op: 'load', delta: -7 },
              { k: 'stat', stat: 'happiness', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'skip',
        label: 'Not this year',
        hint: 'risky',
        tags: ['work'],
        outcomes: [
          {
            weight: 1,
            label: 'Did not take leave',
            narrative: 'You carried the days over. You will carry them over again next year.',
            tone: 'negative',
            effects: [
              { k: 'burnout', op: 'load', delta: 9 },
              { k: 'stat', stat: 'energy', delta: -5 },
              { k: 'habit', habit: 'workaholism', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.hobby',
    title: 'Something entirely for you',
    description:
      'A thing with no professional value whatsoever. It would take two evenings a week and produce nothing marketable.',
    category: 'leisure',
    pools: ['pool.leisure', 'pool.retired.leisure'],
    weight: 7,
    cooldown: 4,
    choices: [
      {
        id: 'start',
        label: 'Start it',
        hint: 'safe',
        tags: ['creative', 'rest'],
        inertiaRisk: 0.25,
        outcomes: [
          {
            weight: 1,
            label: 'Took it up',
            narrative: 'Badly at first, then adequately, then with real and useless pleasure.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 11 },
              { k: 'stat', stat: 'purpose', delta: 7 },
              { k: 'stat', stat: 'stress', delta: -9 },
              { k: 'skill', skill: 'creative', delta: 6 },
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'aspiration', op: 'progress', defId: 'dream.craft', delta: 22 },
            ],
          },
        ],
      },
      {
        id: 'skip',
        label: 'No time',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Let it go',
            narrative: 'Correct, on the available evidence, and slightly sad.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 3 }],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.sundayDread',
    title: 'Sunday evening',
    description: 'The feeling arrives at about six every week. It is information.',
    category: 'wellbeing',
    pools: ['pool.stress'],
    weight: 7,
    cooldown: 5,
    requires: [
      { k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'founder'] },
      { k: 'num', path: 'meaningAlignment', op: '<=', value: 45 },
    ],
    choices: [
      {
        id: 'listen',
        label: 'Treat it as data',
        detail: 'Work out what specifically is wrong.',
        hint: 'mixed',
        tags: ['reflection'],
        outcomes: [
          {
            weight: 1,
            label: 'Diagnosed it',
            narrative: 'It was one person, one project, and one thing you had stopped believing in.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'intention', op: 'add', id: 'int.jobSearch', label: 'Change something about work', topic: 'career', urgency: 38, resumeEventId: 'career.jobSearch', decayEventId: 'career.staleCareer' },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'numb',
        label: 'Distract yourself',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Looked away',
            narrative: 'Television, wine, and a sharpened dread that arrives slightly earlier each week.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -5 },
              { k: 'burnout', op: 'load', delta: 8 },
              { k: 'habit', habit: 'procrastination', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.midlifeReckoning',
    title: 'Is this it?',
    description:
      'Not a crisis. A question that will not go away: if the next twenty years look like the last five, is that acceptable?',
    category: 'wellbeing',
    pools: ['pool.milestone.45', 'pool.regret'],
    weight: 9,
    unique: true,
    requires: [{ k: 'age', min: 42, max: 54 }],
    choices: [
      {
        id: 'change',
        label: 'Change something real',
        hint: 'risky',
        tags: ['change', 'courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Made a change',
            narrative: 'You altered one significant thing, deliberately, and it rearranged the rest.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'trait', trait: 'adaptability', delta: 6 },
              { k: 'intention', op: 'resolve', id: 'int.jobSearch' },
              { k: 'inertia', op: 'pressure', delta: -14 },
              { k: 'triggerEvent', eventId: 'career.pivotOffer', afterYears: 1 },
              { k: 'memory', title: 'Asked whether this was it — and changed something', tone: 'milestone', category: 'life', weight: 8 },
            ],
          },
        ],
      },
      {
        id: 'reframe',
        label: 'Decide this is enough',
        detail: 'Stop chasing. Value what exists.',
        hint: 'safe',
        tags: ['acceptance', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Chose contentment',
            narrative: 'Not resignation. An actual decision to stop measuring your life against a version that never existed.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'stat', stat: 'stress', delta: -10 },
              { k: 'regret', op: 'resolve', id: 'regret.careerMove' },
              { k: 'regret', op: 'resolve', id: 'regret.pivot' },
              { k: 'trait', trait: 'patience', delta: 6 },
              { k: 'memory', title: 'Decided it was enough', tone: 'milestone', category: 'life', weight: 7 },
            ],
          },
        ],
      },
      {
        id: 'nothing',
        label: 'Put the question away',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Did not answer it',
            narrative: 'It went quiet for about four years.',
            tone: 'negative',
            effects: [
              { k: 'inertia', op: 'pressure', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: -8 },
              { k: 'schedule', afterYears: 5, label: 'The question came back', eventId: 'wellbeing.lateRegret', effects: [] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'wellbeing.lateRegret',
    title: 'The question came back',
    description: 'Later, with less time to do anything about it.',
    category: 'wellbeing',
    pools: ['pool.regret'],
    weight: 0,
    choices: [
      {
        id: 'act',
        label: 'Act on it now',
        hint: 'risky',
        tags: ['courage', 'late'],
        outcomes: [
          {
            weight: 1,
            label: 'Better late',
            narrative: 'Fifteen years later than ideal, and still better than never.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 16 },
              { k: 'counter', name: 'lateChanges', delta: 1 },
              { k: 'regret', op: 'resolve', id: 'regret.careerMove' },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'accept',
        label: 'Accept the shape of it',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Let it be',
            narrative: 'Some questions are answered by running out of time to ask them.',
            tone: 'neutral',
            effects: [
              { k: 'regret', op: 'add', id: 'regret.unanswered', label: 'Never answered the question', weight: 18 },
              { k: 'stat', stat: 'happiness', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
];
